import { Typography } from "@mui/material";
import type { GridApiPremium, GridCellParams, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { isAutoGeneratedRowNode } from "@entrilia/shared/components/grid/gridHelper";
import { getRowNode } from "@entrilia/shared/utilities/dataGridApiHelper.ts";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { OrganizationReportInfo, ReportInfo } from "../../../../../api/types/svTypes.ts";
import SharingCell from "../../../reporting/report-lists/reports/grid/SharingCell";
import OrganizationGroupSharingCell from "./OrganizationGroupSharingCell";

export const getSharingColumn = (shareReport: (report: ReportInfo) => void): GridColDef<OrganizationReportInfo> => ({
  field: nameof<OrganizationReportInfo>("authorization"),
  headerName: "Sharing",
  flex: 0.3,
  valueGetter: (_, row) => row.authorization?.private ?? false,
  renderCell: (params: GridRenderCellParams<OrganizationReportInfo>) => {
    if (params.rowNode.type === "group") {
      // Tree is [clientCode, groupName, reportId]: depth 1 = report-group folder (shareable); depth 0 = client folder.
      return params.rowNode.depth === 1 ? <OrganizationGroupSharingCell params={params} /> : null;
    }
    return <SharingCell report={params.row} onShareClick={shareReport} />;
  },
});

export const reportTypeColumn = {
  field: nameof<OrganizationReportInfo>("reportType"),
  headerName: "Report Type",
  flex: 0.3,
};

export const createdByColumn = {
  field: nameof<OrganizationReportInfo>("createdBy"),
  headerName: "Created by",
  flex: 0.5,
};

export const modifiedByColumn = {
  field: nameof<OrganizationReportInfo>("modifiedBy"),
  headerName: "Modified by",
  flex: 0.5,
};

export const getModifiedAtColumn = (apiRef: RefObject<GridApiPremium | null>): GridColDef => ({
  field: nameof<OrganizationReportInfo>("updateAt"),
  headerName: "Modified at",
  flex: 0.4,
  renderCell: ({ id, value }: GridCellParams<OrganizationReportInfo, string>) => {
    const treeNode = getRowNode(apiRef, id || "");
    if (treeNode && isAutoGeneratedRowNode(treeNode)) {
      return null;
    }

    return <Typography color="secondary">{convertISODateTime(value)}</Typography>;
  },
});
